import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Lightbulb, Search, Heart, Gauge, Sparkles,
  HelpCircle, Users, AlertCircle, Compass, Target,
  ChevronRight, Check, ArrowRight, Church, UserCog, HandHeart, Building2
} from 'lucide-react';
import { motion } from 'framer-motion';
import ForgotPasswordModal from '../components/ForgotPasswordModal';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

const LandingPage = ({ auth }) => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [registrationType, setRegistrationType] = useState('individual');
  const [hasOrgCode, setHasOrgCode] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    
    const payload = {
      name: formData.get('name'),
      email: formData.get('email'),
      password: formData.get('password'),
      registration_type: registrationType
    };
    
    if (registrationType === 'organization') {
      payload.organization_name = formData.get('organization_name');
    } else if (hasOrgCode) {
      payload.organization_join_code = formData.get('organization_join_code');
    }
    
    try {
      const response = await axios.post(`${API}/auth/register`, payload);
      auth.login(response.data.token, response.data.user);
      if (registrationType === 'organization') {
        toast.success('Organization created! Welcome to your admin dashboard');
      } else {
        toast.success('Welcome! Let\'s discover your BUILDR profile');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.get('email'),
        password: formData.get('password')
      });
      auth.login(response.data.token, response.data.user);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (auth.user) {
    navigate('/dashboard');
    return null;
  }

  const dimensions = [
    { icon: Lightbulb, name: 'Brainstorm', code: 'B', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', desc: 'Visionary thinking and asking big questions' },
    { icon: Search, name: 'Uncover', code: 'U', color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200', desc: 'Creating and improving solutions' },
    { icon: Target, name: 'Inspect', code: 'I', color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200', desc: 'Evaluating and making sound decisions' },
    { icon: Heart, name: 'Lift', code: 'L', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', desc: 'Supporting and empowering others' },
    { icon: Gauge, name: 'Deliver', code: 'D', color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200', desc: 'Driving results and execution' },
    { icon: Sparkles, name: 'Rally', code: 'R', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', desc: 'Motivating and mobilizing people' }
  ];

  const painPoints = [
    { icon: AlertCircle, title: 'Wrong Roles', desc: 'People placed where they don\'t thrive' },
    { icon: Users, title: 'Burnout', desc: 'Leaders and volunteers exhausted' },
    { icon: Compass, title: 'No Direction', desc: 'Teams guessing instead of knowing' }
  ];

  const steps = [
    { num: '01', title: 'Take the Assessment', desc: 'Answer 42 questions about how you naturally work and serve' },
    { num: '02', title: 'Discover Your Strengths', desc: 'Get your personalized BUILDR profile with detailed insights' },
    { num: '03', title: 'Find Where You Thrive', desc: 'Receive role recommendations matched to your wiring' },
    { num: '04', title: 'Build Healthier Teams', desc: 'Create balanced teams with complementary strengths' }
  ];

  const audiences = [
    { icon: Church, title: 'Churches', desc: 'Place volunteers and staff in roles where they\'ll flourish and grow' },
    { icon: UserCog, title: 'Pastors & Leaders', desc: 'Understand your team\'s strengths and build with intention' },
    { icon: HandHeart, title: 'Volunteers', desc: 'Discover where you\'re wired to serve and make the greatest impact' },
    { icon: Building2, title: 'Teams & Orgs', desc: 'Align your people with purpose for greater effectiveness' },
    { icon: Target, title: 'Marketplace Leaders & Business Owners', desc: 'Build high-performing teams and maximize your organization\'s potential' }
  ];

  const outcomes = [
    'Clear placement decisions',
    'Stronger, balanced teams',
    'Less burnout, more fulfillment',
    'Better leadership decisions',
    'Healthier organizational culture',
    'Higher volunteer retention'
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="font-display text-2xl font-bold text-slate-900 tracking-tight">BUILDR</div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="landing-help-btn"
              onClick={() => navigate('/help')}
              variant="ghost"
              className="text-slate-600 hover:text-slate-900 text-sm font-medium"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Help
            </Button>
            <Button
              data-testid="nav-login-btn"
              onClick={() => setShowAuth(true)}
              variant="outline"
              className="rounded-full px-6 border-slate-200 hover:bg-slate-50 text-sm font-medium"
            >
              Login
            </Button>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Left Content */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="text-left"
            >
              <motion.h1 
                variants={fadeInUp}
                className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-tight tracking-tight mb-6"
              >
                Clarity for People.
                <br />
                <span className="text-slate-500">Alignment for Teams.</span>
              </motion.h1>
              <motion.p 
                variants={fadeInUp}
                className="text-lg md:text-xl text-slate-600 mb-10 max-w-lg leading-relaxed"
              >
                Discover how you're wired to serve and lead. The BUILDR framework helps bring clarity to leaders and teams across churches, businesses, and organizations, placing the right people in the right roles.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button 
                  data-testid="get-started-btn"
                  onClick={() => setShowAuth(true)} 
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  Take the Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                  className="rounded-full px-8 py-6 text-lg font-medium border-slate-200 hover:bg-slate-50 transition-all duration-300"
                >
                  Learn More
                </Button>
              </motion.div>
            </motion.div>

            {/* Right Visual */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative flex justify-center"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-slate-50 p-4 max-w-sm">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/31143310-4f5a-43e7-aa19-9c26da3ce850/images/0902b690831f30fc3ab39a61b657b85c4a1e9069f05edbf430ef8345abc3573d.png"
                  alt="BUILDR Assessment Report on iPad"
                  className="w-full h-auto object-contain"
                />
              </div>
              {/* Floating Stats Card */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 border border-slate-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-orange-700" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">10 min</p>
                    <p className="text-sm text-slate-500">Assessment time</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-orange-700 font-semibold tracking-widest text-xs uppercase mb-4">
              The Challenge
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Sound Familiar?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-slate-600 max-w-2xl mx-auto">
              Too many teams struggle with misalignment. Great people in the wrong seats leads to frustration for everyone.
            </motion.p>
          </motion.div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {painPoints.map((point, idx) => (
              <motion.div 
                key={idx}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-6">
                  <point.icon className="w-7 h-7 text-slate-700" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{point.title}</h3>
                <p className="text-slate-600">{point.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Framework Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-orange-700 font-semibold tracking-widest text-xs uppercase mb-4">
              The Solution
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              The BUILDR Framework
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-slate-600 max-w-2xl mx-auto">
              Six dimensions that reveal how you're naturally wired to work, lead, and serve.
            </motion.p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {dimensions.map((dim, idx) => (
              <motion.div 
                key={dim.code}
                variants={scaleIn}
                className={`group relative bg-white rounded-2xl p-8 border ${dim.border} hover:border-slate-300 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 ${dim.bg} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                    <dim.icon className={`w-7 h-7 ${dim.color}`} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl font-bold text-slate-300">{dim.code}</span>
                      <h3 className="text-xl font-semibold text-slate-900">{dim.name}</h3>
                    </div>
                    <p className="text-slate-600">{dim.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-orange-700 font-semibold tracking-widest text-xs uppercase mb-4">
              Simple Process
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              How It Works
            </motion.h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {steps.map((step, idx) => (
              <motion.div 
                key={idx}
                variants={fadeInUp}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm h-full">
                  <span className="text-5xl font-bold text-slate-100 absolute top-4 right-6">{step.num}</span>
                  <div className="relative z-10">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">{step.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <ChevronRight className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 text-slate-300 z-20" />
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-orange-700 font-semibold tracking-widest text-xs uppercase mb-4">
              Built For You
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Who It's For
            </motion.h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {audiences.slice(0, 4).map((audience, idx) => (
              <motion.div 
                key={idx}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center"
              >
                <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <audience.icon className="w-8 h-8 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{audience.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{audience.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          {/* Centered 5th item */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="flex justify-center mt-6"
          >
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center w-full md:w-1/2 lg:w-1/4">
              <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">{audiences[4].title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{audiences[4].desc}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-24 md:py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeInUp}>
              <p className="text-orange-400 font-semibold tracking-widest text-xs uppercase mb-4">
                Real Results
              </p>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
                What You'll Achieve
              </h2>
              <p className="text-lg text-slate-400 mb-10 leading-relaxed">
                BUILDR doesn't just assess—it transforms how your team operates and how your people experience their roles.
              </p>
              <Button 
                onClick={() => setShowAuth(true)}
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-medium shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {outcomes.map((outcome, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-4"
                  >
                    <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-white font-medium">{outcome}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Ready to Discover Your BUILDR Profile?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
              Join thousands of leaders and teams who've found clarity, alignment, and purpose through BUILDR.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button 
                data-testid="final-cta-btn"
                onClick={() => setShowAuth(true)}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-10 py-7 text-xl font-medium shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Take the Assessment Now
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <div className="font-display text-xl font-bold text-white mb-2">BUILDR</div>
              <p className="text-sm">Clarity for People. Alignment for Teams.</p>
            </div>
            <div className="flex items-center gap-8">
              <button 
                onClick={() => navigate('/help')} 
                className="text-sm hover:text-white transition-colors"
              >
                Help & Support
              </button>
              <p className="text-sm">
                © {new Date().getFullYear()} BUILDR™. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-3xl max-w-md w-full p-8 relative shadow-2xl"
          >
            <button
              data-testid="close-auth-modal"
              onClick={() => setShowAuth(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 text-2xl transition-colors"
            >
              ×
            </button>
            <Tabs defaultValue="register" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100 rounded-full p-1">
                <TabsTrigger value="register" data-testid="register-tab" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">Register</TabsTrigger>
                <TabsTrigger value="login" data-testid="login-tab" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">Login</TabsTrigger>
              </TabsList>
              
              <TabsContent value="register">
                <form onSubmit={handleRegister} data-testid="register-form">
                  <div className="space-y-4">
                    {/* Registration Type */}
                    <div>
                      <Label className="mb-3 block text-slate-700">I am registering as:</Label>
                      <div className="space-y-2">
                        <div 
                          onClick={() => {
                            setRegistrationType('individual');
                            setHasOrgCode(false);
                          }}
                          className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            registrationType === 'individual' 
                              ? 'border-slate-900 bg-slate-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid="individual-option"
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="registration_type"
                              checked={registrationType === 'individual'}
                              onChange={() => {
                                setRegistrationType('individual');
                                setHasOrgCode(false);
                              }}
                              className="w-4 h-4 accent-slate-900"
                            />
                            <div>
                              <div className="font-semibold text-slate-900">Individual</div>
                              <div className="text-sm text-slate-500">Take the assessment for myself</div>
                            </div>
                          </div>
                        </div>
                        
                        {registrationType === 'individual' && (
                          <div className="ml-8 mt-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={hasOrgCode}
                                onChange={(e) => setHasOrgCode(e.target.checked)}
                                className="w-4 h-4 accent-slate-900"
                                data-testid="has-org-code-checkbox"
                              />
                              <span className="text-sm text-slate-600">I have an organization code</span>
                            </label>
                          </div>
                        )}
                        
                        <div 
                          onClick={() => setRegistrationType('organization')}
                          className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            registrationType === 'organization' 
                              ? 'border-slate-900 bg-slate-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid="organization-option"
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="registration_type"
                              checked={registrationType === 'organization'}
                              onChange={() => setRegistrationType('organization')}
                              className="w-4 h-4 accent-slate-900"
                            />
                            <div>
                              <div className="font-semibold text-slate-900">Organization / Church</div>
                              <div className="text-sm text-slate-500">Manage volunteers and team assessments</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {registrationType === 'organization' && (
                      <div>
                        <Label htmlFor="organization_name" className="text-slate-700">Organization Name</Label>
                        <Input 
                          id="organization_name" 
                          name="organization_name" 
                          placeholder="e.g., First Community Church"
                          required 
                          data-testid="register-org-name"
                          className="mt-1 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                        />
                      </div>
                    )}

                    {hasOrgCode && registrationType === 'individual' && (
                      <div>
                        <Label htmlFor="organization_join_code" className="text-slate-700">Organization Code</Label>
                        <Input 
                          id="organization_join_code" 
                          name="organization_join_code" 
                          placeholder="Enter 8-character code"
                          maxLength="8"
                          required 
                          data-testid="register-join-code"
                          className="mt-1 uppercase rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                        />
                        <p className="text-sm text-slate-500 mt-1">Get this code from your church administrator</p>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="name" className="text-slate-700">{registrationType === 'organization' ? 'Your Name' : 'Full Name'}</Label>
                      <Input id="name" name="name" required data-testid="register-name" className="mt-1 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-700">Email</Label>
                      <Input id="email" name="email" type="email" required data-testid="register-email" className="mt-1 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400" />
                    </div>
                    <div>
                      <Label htmlFor="password" className="text-slate-700">Password</Label>
                      <Input id="password" name="password" type="password" required data-testid="register-password" className="mt-1 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400" />
                    </div>
                    <Button 
                      data-testid="register-submit"
                      type="submit" 
                      className="w-full bg-slate-900 hover:bg-slate-800 rounded-full py-6 text-base font-medium transition-all" 
                      disabled={loading}
                    >
                      {loading ? 'Creating Account...' : registrationType === 'organization' ? 'Create Organization' : 'Create Account'}
                    </Button>
                  </div>
                </form>
              </TabsContent>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} data-testid="login-form">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="login-email" className="text-slate-700">Email</Label>
                      <Input id="login-email" name="email" type="email" required data-testid="login-email" className="mt-1 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400" />
                    </div>
                    <div>
                      <Label htmlFor="login-password" className="text-slate-700">Password</Label>
                      <Input id="login-password" name="password" type="password" required data-testid="login-password" className="mt-1 rounded-xl border-slate-200 focus:border-slate-400 focus:ring-slate-400" />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-slate-600 hover:text-slate-900 hover:underline transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <Button 
                      data-testid="login-submit"
                      type="submit" 
                      className="w-full bg-slate-900 hover:bg-slate-800 rounded-full py-6 text-base font-medium transition-all" 
                      disabled={loading}
                    >
                      {loading ? 'Logging in...' : 'Login'}
                    </Button>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      )}

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </div>
  );
};

export default LandingPage;
